import { GuidUtil } from '@liberty-financial/ng-core';

import { Individual, Relationship } from '@features/contacts/models';
import { Applicant } from '@features/opportunities/modules/applicant/services/applicant';
import { BaseApplicant } from '@features/opportunities/modules/applicant/services/base-applicant';
import { OpportunitySchema } from '@features/opportunities/modules/core/models/schema-property-definition';
import { OpportunitySchemaUtil } from '@features/opportunities/modules/core/utils/opportunity-schema-util';

import { Contact } from './contact';
import { CreditProfile } from './credit-profile';
import { Dependent } from './dependent';
import { Identification } from './identification';
import { Name } from './name';
import { RelationshipStatus } from './relationship-status';
import { Residence } from './residence';

export namespace IndividualApplicant {
  export interface SchemaPropertyDefinition
    extends BaseApplicant.SchemaPropertyDefinition {
    bankingInstitution: OpportunitySchema.StringPropertyDefinition;
    birthDate: OpportunitySchema.StringPropertyDefinition;
    citizen: OpportunitySchema.BooleanPropertyDefinition;
    countryOfBirth: OpportunitySchema.StringPropertyDefinition;
    creditProfile: CreditProfile.SchemaPropertyDefinition;
    gender: OpportunitySchema.OptionsPropertyDefinition;
    identification: Identification.SchemaPropertyDefinition;
    name: Name.SchemaPropertyDefinition;
    permanentResident: OpportunitySchema.BooleanPropertyDefinition;
    repayingStudentLoan: OpportunitySchema.BooleanPropertyDefinition;
    relationshipStatus: RelationshipStatus.SchemaPropertyDefinition;
    contact: Contact.SchemaPropertyDefinition;
  }

  export interface SchemaContract extends BaseApplicant.SchemaContract {
    bankingInstitution?: string | null;
    birthDate?: string | null;
    citizen?: boolean | null;
    countryOfBirth?: string | null;
    creditProfile?: CreditProfile.SchemaContract;
    gender?: string | null;
    identification: Identification.SchemaContract;
    name: Name.SchemaContract;
    permanentResident?: boolean | null;
    repayingStudentLoan?: boolean | null;
    relationshipStatus: RelationshipStatus.SchemaContract;
    contact: Contact.SchemaContract;
    residences: Residence.Collection.SchemaContract;
  }

  export interface Model extends BaseApplicant.Model {
    id: string;
    bankingInstitution: string | null;
    birthDate: string | null;
    citizen: boolean | null;
    countryOfBirth: string | null;
    creditProfile: CreditProfile.Model;
    gender: string | null;
    identification: Identification.Model;
    name: Name.Model;
    permanentResident: boolean | null;
    repayingStudentLoan: boolean | null;
    relationshipStatus: RelationshipStatus.Model;
    contact: Contact.Model;
    residences: Residence.Collection.Model;
  }

  export function toSchemaContract(
    model: Partial<Model>
  ): Partial<SchemaContract> {
    return {
      ...model,
      identification: model.identification
        ? Identification.toSchemaContract(model.identification)
        : undefined,
      name: model.name ? Name.toSchemaContract(model.name) : undefined,
      relationshipStatus: model.relationshipStatus
        ? RelationshipStatus.toSchemaContract(model.relationshipStatus)
        : undefined,
      contact: model.contact
        ? Contact.toSchemaContract(model.contact)
        : undefined,
      residences: model.residences
        ? Residence.Collection.toSchemaContract(model.residences)
        : undefined,
      creditProfile: model.creditProfile
        ? CreditProfile.toSchemaContract(model.creditProfile)
        : undefined,
      birthDate: OpportunitySchemaUtil.Transform.Date.toSchemaContract(
        model.birthDate
      )
    };
  }

  export function toModel(id: string, contract: SchemaContract): Model {
    return {
      id,
      role: contract.role ?? null,
      bankingInstitution: contract.bankingInstitution ?? null,
      birthDate: contract.birthDate ?? null,
      citizen: contract.citizen ?? null,
      countryOfBirth: contract.countryOfBirth ?? null,
      gender: contract.gender ?? null,
      permanentResident: contract.permanentResident ?? null,
      repayingStudentLoan: contract.repayingStudentLoan ?? null,
      identification: Identification.toModel(contract.identification ?? {}),
      name: Name.toModel(contract.name ?? {}),
      relationshipStatus: RelationshipStatus.toModel(
        contract.relationshipStatus
      ),
      contact: Contact.toModel(contract.contact ?? {}),
      residences: Residence.Collection.toModel(contract.residences ?? {}),
      creditProfile: CreditProfile.toModel(contract.creditProfile ?? {})
    };
  }

  function mapRelationshipStatusFromContact(relationships: Relationship.Model[]): RelationshipStatus.Model {
    const hasSpouse = relationships?.some(r => r.value?.toLowerCase() === 'spouse');
    return hasSpouse 
      ? { status: RelationshipStatus.Status.NonApplicantPartner, nonApplicantExpenses: null }
      : {} as RelationshipStatus.Model;
  }

  export function copyFromContact(individual: Individual.Model): Model {
    return {
      id: GuidUtil.generate(),
      role: null,
      relationshipStatus: mapRelationshipStatusFromContact(individual.relationships),
      name: individual.name,
      contact: individual.contact,
      gender: individual.gender,
      birthDate: individual.birthDate,
      countryOfBirth: individual.countryOfBirth,
      identification: Identification.toModel(individual.identification ?? {}),
      bankingInstitution: individual.bankingInstitution,
      permanentResident: individual.permanentResident,
      citizen: individual.citizen,
      repayingStudentLoan: individual.repayingStudentLoan,
      creditProfile: CreditProfile.toModel({}),
      residences: individual.residences.map(
        (r) =>
          ({
            id: r.id,
            address: r.address,
            addressType: r.addressType,
            residentialStatus: r.residentialStatus,
            startDate: r.startDate,
            endDate: r.endDate,
            created: true,
            deleted: false
          } as Residence.Model)
      )
    };
  }

  export interface Summary {
    id: string;
    entityId: string;
    firstName: string;
    lastName: string;
    displayName: string;
    role: string;
    email?: string;
    mobile?: string;
  }

  export namespace SchemaContract {
    export function getDisplayName(c: SchemaContract): string {
      const { first, last, title, middle } = c.name;
      return [title || '', first || '', middle || '', last || '']
        .filter((x) => !!x)
        .join(' ');
    }

    export function toSummary(id: string, c: SchemaContract): Summary {
      return {
        id: `individuals/${id}`,
        entityId: id,
        firstName: c.name.first ?? '',
        lastName: c.name.last ?? '',
        displayName: getDisplayName(c),
        role: c.role ?? '',
        email: c.contact?.email ?? '',
        mobile: c.contact?.mobile ?? ''
      };
    }

    export function toApplicantSummary(
      id: string,
      c: SchemaContract
    ): Applicant.Summary {
      return {
        id: `individuals/${id}`,
        entityId: id,
        displayName: getDisplayName(c),
        email: c.contact?.email ?? '',
        applicantType: Applicant.Type.Individual,
        contactNumber: c.contact?.mobile || '',
        description: '',
        role: c.role ?? ''
      };
    }
  }

  export namespace IndividualWithDependents {
    export interface SchemaContract {
      individual: IndividualApplicant.SchemaContract;
      dependents: Dependent.Collection.SchemaContract;
    }

    export interface Model {
      individual: IndividualApplicant.Model;
      dependents: Dependent.Collection.Model;
    }

    export type PartialSchemaContract = Partial<{
      individual: Partial<IndividualApplicant.Model>;
      dependents: Dependent.Collection.Model;
    }>;
  }

  export namespace Model {
    export function hasCurrentAddress(individual: Model): boolean {
      return (
        !!individual.residences?.find(
          (r) => r.addressType == 'Residential' && !!r.startDate && !r.endDate
        )
      );
    }
  }
}
